export type SammelnGeschlecht = 'm' | 'w';

export type SammelnHerkunftFields = {
    herkunft_vvvo: string;
    herkunft_betrieb: string;
    herkunft_strasse: string;
    herkunft_plz: string;
    herkunft_ort: string;
    herkunft_ortsteil: string;
};

export type SammelnHerkunftOption = {
    label: string;
    herkunftKundeId?: number | null;
    fields: SammelnHerkunftFields;
};

export type SammelnTier = {
    ohrmarke: string;
    geschlecht: SammelnGeschlecht | null;
    gewicht: string;
    haendler: string | null;
    haendlerId?: number | null;
    herkunft: string | null;
    herkunftKundeId?: number | null;
    herkunftFields?: SammelnHerkunftFields | null;
    abteil: string | null;
    bemerkungen: string[];
    reklamationen: string[];
    reklamationIds?: number[];
    gtId?: number | null;
    sourceGruppeId?: number | null;
    baselineBemerkungen?: string[];
    saved?: boolean;
};

export function emptyHerkunftFields(): SammelnHerkunftFields {
    return {
        herkunft_vvvo: '',
        herkunft_betrieb: '',
        herkunft_strasse: '',
        herkunft_plz: '',
        herkunft_ort: '',
        herkunft_ortsteil: '',
    };
}

/** Sticky defaults for the next new (unknown) scan in Sammelstelle. */
export type SammelnTierDefaults = {
    abteil?: string | null;
    bemerkungen?: string[];
    reklamationen?: string[];
    reklamationIds?: number[];
};

export function emptySammelnTier(
    ohrmarke: string,
    defaults?: SammelnTierDefaults | string | null,
): SammelnTier {
    const d: SammelnTierDefaults =
        typeof defaults === 'string' || defaults == null || defaults === undefined
            ? { abteil: (defaults as string | null | undefined) ?? null }
            : defaults;
    return {
        ohrmarke: String(ohrmarke ?? '').replace(/\D/g, ''),
        geschlecht: null,
        gewicht: '',
        haendler: null,
        haendlerId: null,
        herkunft: null,
        herkunftKundeId: null,
        herkunftFields: null,
        abteil: d.abteil ?? null,
        bemerkungen: [...(d.bemerkungen ?? [])],
        reklamationen: [...(d.reklamationen ?? [])],
        reklamationIds: [...(d.reklamationIds ?? [])],
        gtId: null,
        sourceGruppeId: null,
        baselineBemerkungen: [],
        saved: false,
    };
}

export function formatOhrmarkeDisplay(ohrmarke: string): string {
    const digits = String(ohrmarke ?? '').replace(/\D/g, '');
    if (digits.length === 15) {
        return `${digits.slice(0, 5)} ${digits.slice(5, 10)} ${digits.slice(10)}`;
    }
    if (digits.length === 10) {
        return `${digits.slice(0, 5)} ${digits.slice(5)}`;
    }
    return digits;
}

export function parseGewichtKg(value: string): number | null {
    const raw = String(value ?? '')
        .trim()
        .replace(',', '.');
    if (!raw) return null;
    const n = Number(raw);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
}

export function tippeGewicht(current: string, key: string): string {
    if (key === 'back' || key === '⌫') {
        return current.slice(0, -1);
    }
    if (key === ',' || key === '.') {
        return current.includes(',') ? current : current + ',';
    }
    if (!/^\d$/.test(key)) {
        return current;
    }
    return current + key;
}

/**
 * Shortcut chips in stable list order (mobile takeShortcuts parity).
 * Selected values stay where they are; selected items outside the first `limit` are appended.
 */
export function takeShortcuts(
    labels: string[],
    selected: string | null | string[],
    limit: number,
): string[] {
    const selectedList = (Array.isArray(selected) ? selected : selected ? [selected] : [])
        .map(s => String(s ?? '').trim())
        .filter(Boolean);

    const seen = new Set<string>();
    const base: string[] = [];
    for (const raw of labels) {
        const t = String(raw ?? '').trim();
        if (!t || seen.has(t)) continue;
        seen.add(t);
        base.push(t);
    }

    const capped = Math.max(0, limit);
    const visible = base.slice(0, capped);
    for (const s of selectedList) {
        if (!visible.includes(s)) visible.push(s);
    }
    return visible;
}

export function herkunftFieldsFromBetriebskontakt(k: {
    vvvo?: string | null;
    bezeichnung_final?: string | null;
    bezeichnung?: string | null;
    strasse_final?: string | null;
    strasse?: string | null;
    plz_final?: string | null;
    plz?: string | null;
    ort_final?: string | null;
    ort?: string | null;
    ortsteil_final?: string | null;
    ortsteil?: string | null;
}): SammelnHerkunftFields {
    return {
        herkunft_vvvo: String(k.vvvo || ''),
        herkunft_betrieb: String(k.bezeichnung_final || k.bezeichnung || ''),
        herkunft_strasse: String(k.strasse_final || k.strasse || ''),
        herkunft_plz: String(k.plz_final ?? k.plz ?? ''),
        herkunft_ort: String(k.ort_final || k.ort || ''),
        herkunft_ortsteil: String(k.ortsteil_final || k.ortsteil || ''),
    };
}
